import {
  Component,
  EventEmitter,
  inject,
  InjectionToken,
  OnDestroy,
  Output,
  SecurityContext,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { SharedModule } from '@app/shared/shared.module';
import { ConnectionService } from '@fmnts/common';
import { Logger, logger } from '@fmnts/common/log';
import { isString } from '@fmnts/core';
import { fromPortMessage } from '@fmnts/core/rxjs';
import { faMoneyBillTransfer } from '@fortawesome/pro-regular-svg-icons';
import { faCloudSlash, faWifiSlash } from '@fortawesome/pro-solid-svg-icons';
import * as Sentry from '@sentry/angular';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  delay,
  EMPTY,
  filter,
  map,
  merge,
  of,
  ReplaySubject,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { RaiseNowModalService } from '../raisenow-modal/raisenow-modal.service';

/** Source of a tamaro message. */
enum TamaroMessageSource {
  /** Host window that hosts the iframe running Tamaro. */
  Host = 'host',
  /** Iframe window that runs Tamaro. */
  Iframe = 'iframe',
  /** Tamaro itself. */
  Tamaro = 'tamaro',
}

enum TamaroMessageType {
  /** Dispatches an action. */
  Action = 'action',
  /** Some error occured. */
  Error = 'error',
  /** An event was called. */
  Event = 'event',
  /** A result message. */
  Result = 'result',
}

/**
 * Actions that the tamaro iframe can handle.
 */
enum TamaroIframeAction {
  ValidateAndSend = 'payment_form.validate_and_send',
}

enum TamaroResult {
  EpmsSuccess = 'epms_success',
  EpmsFailed = 'epms_failed',
}

/**
 * Events dispatched from the tamaro instance.
 *
 * @see {@link https://unpkg.com/@raisenow/tamaro-core@2.6.0/dist/readme.html#/?id=events}
 */
enum TamaroEvent {
  AfterRender = 'afterRender',
  PaymentValidateError = 'paymentValidateError',
  BeforePaymentValidateAndSend = 'beforePaymentValidateAndSend',
  BeforePaymentSend = 'beforePaymentSend',
  FetchPaymentDataStart = 'fetchPaymentDataStart',
  FetchPaymentDataEnd = 'fetchPaymentDataEnd',
}

interface TamaroMessage {
  /** Message type */
  type: TamaroMessageType;
  /** Source of the message */
  source: TamaroMessageSource;
  /** Name that identifies the message */
  name: string;
}

enum State {
  /** Not yet initialized. */
  Uninitialized,
  /** Content is ready. */
  Ready,
  /** Validation or Payment is in progress. */
  InProgress,
  /** Indicates whether payment was successful. */
  Done,
  /** Some error occured. */
  Failed,
}

const SENTRY_TAMARO_CATEGORY = 'rnw.tamaro';
enum SentryTamaroTag {
  Instance = 'rnw.tamaro.instance',
}

interface TamaroWidgetConfig {
  /** ID for the tamaro widget instance that should be loaded. */
  tamaroInstance: string;
  /** Configuration for tamaro widget. */
  tamaroConfig: Record<string, unknown>;
  /**
   * When set to `true`, this will cleanup any session data from previous
   * payment attempts.
   */
  cleanupSession: boolean;
}

class TamaroError extends Error {
  override name = 'TamaroError';
  constructor(
    public readonly source: TamaroMessageSource,
    message: string,
  ) {
    super(`from ${source}: ${message}`);
  }
}

/**
 * Options for RaiseNow Tamaro.
 */
interface RaiseNowTamaroIframeConfig {
  /** Delay before cancel button can be clicked. */
  cancelDelay: number;
  /**
   * If `true` the session should be cleaned in Tamaro before
   * initialization.
   * Otherwise Tamaro will try to load the config based on data stored in
   * the session from previous payments.
   */
  cleanupSession: boolean;
  /**
   * This flag is used to determine if the submit button for the payment
   * should be shown inside Tamaro or in the modal of the main app.
   *
   * This is due to limitations with Apple Pay that can't be invoked
   * via `postMessage` to the iframe, but must be triggered via a
   * user interaction.
   */
  useTamaroSubmitButton: boolean;
}

/** Injection token that can be used to specify default raisenow tamaro options. */
const FMNTS_RAISENOW_TAMARO_DEFAULT_OPTIONS = new InjectionToken<
  Readonly<RaiseNowTamaroIframeConfig>
>('@fmnts.shared.raisenow.tamaro.default-options');

/**
 * IFrame component, to host the new RaiseNow payment platform Tamaro.
 */
@Component({
  selector: 'next-raisenow-tamaro-iframe',
  templateUrl: './raisenow-iframe-tamaro.component.html',
  styleUrls: ['./raisenow-iframe-tamaro.component.scss'],
  standalone: true,
  imports: [SharedModule],
})
export class RaiseNowIframeTamaroComponent implements OnDestroy {
  private readonly connectionService = inject(ConnectionService);
  private readonly raiseNowModalService = inject(RaiseNowModalService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly _logs: Readonly<Record<TamaroMessageSource, Logger>> = {
    [TamaroMessageSource.Host]: logger({
      name: TamaroMessageSource.Host,
      category: SENTRY_TAMARO_CATEGORY,
    }),
    [TamaroMessageSource.Iframe]: logger({
      name: TamaroMessageSource.Iframe,
      category: SENTRY_TAMARO_CATEGORY,
    }),
    [TamaroMessageSource.Tamaro]: logger({
      name: TamaroMessageSource.Tamaro,
      category: SENTRY_TAMARO_CATEGORY,
    }),
  };

  /** Raisenow tamaro options. */
  private readonly _config = inject(FMNTS_RAISENOW_TAMARO_DEFAULT_OPTIONS, {
    optional: true,
  }) ?? {
    cancelDelay: 60 * 1000,
    cleanupSession: true,
    useTamaroSubmitButton: true,
  };

  /** Configuration for the tamaro widget to be transfered to the iframe. */
  private readonly widgetConfig: TamaroWidgetConfig;

  protected isOnline = true;
  protected uploadError = false;

  protected readonly faOfflineMode = faWifiSlash;
  protected readonly faUploadError = faCloudSlash;
  protected readonly _iconStartPayment = faMoneyBillTransfer;

  // TODO(modal): Remove when using FmntsModal
  /** Emits when the modal should be closed. */
  @Output() closed = new EventEmitter<any>();

  // TODO(modal): Replace with result from `ModalRef`
  /** Emits when payment was finished. */
  @Output() finished = new EventEmitter<void>();

  protected _isOnline$ = this.connectionService.connectionStatus$.pipe(
    map((connectionStatus) => connectionStatus === 'online'),
  );

  /** Subject for receiving messages from iframe / tamaro. */
  private _msgIn$ = new Subject<TamaroMessage>();
  /** Subject for sending messages to iframe / tamaro. */
  private _msgOut$ = new Subject<TamaroMessage>();

  private _destroyed$ = new ReplaySubject<boolean>(1);
  /** Once set to true, the iframe should be destroyed  */
  private _frameDestroyed$ = new ReplaySubject<true>(1);
  /** Emits immediately with whether the iframe should be available  */
  protected hasFrame$ = this._frameDestroyed$.pipe(
    startWith(false),
    map((v) => !v),
  );

  // TODO(tamaro): extract in a component Store
  /** Emits the state that the payment process is currently in. */
  private readonly _state$ = new BehaviorSubject(State.Uninitialized);

  /**
   * Emits `true`, once the payment process has a final result.
   * In this case the modal must be opened again.
   */
  protected readonly _finalState$ = this._state$.pipe(
    filter((s) => [State.Done, State.Failed].includes(s)),
    take(1),
  );
  /** Emits `true`, when state is `Ready` */
  protected readonly _isReady$ = this._state$.pipe(
    map((state) => state === State.Ready),
  );
  /** Emits `true` if payment is in progress. */
  protected readonly _paymentInProgress$ = this._state$.pipe(
    map((state) => state === State.InProgress),
  );
  protected readonly _canStartPayment$ = combineLatest([
    this._isOnline$,
    this._isReady$,
  ]).pipe(map(([isOnline, isReady]) => isOnline && isReady));

  /** Emits `true` if the close button should be disabled. */
  protected readonly _closeDisabled$ = this._paymentInProgress$.pipe(
    switchMap((paymentInProgress) =>
      paymentInProgress
        ? of(false).pipe(delay(this._config.cancelDelay), startWith(true))
        : of(false),
    ),
  );

  /** `true` if the iframe is initializing. */
  protected get isInitializing() {
    return this._state$.value === State.Uninitialized;
  }

  /**
   * This flag is used to determine if the submit button for the payment
   * should be shown inside Tamaro or in the modal of the main app.
   */
  protected readonly useTamaroSubmitButton = this._config.useTamaroSubmitButton;

  constructor() {
    // Keep isOnline
    this._isOnline$
      .pipe(takeUntil(this._destroyed$))
      .subscribe((isOnline) => (this.isOnline = isOnline));

    // Setup listening for messages
    this._msgIn$
      .pipe(
        catchError((e) => {
          Sentry.captureException(e);
          this._state$.next(State.Failed);
          this.uploadError = true;
          this._destroyFrame();
          return EMPTY;
        }),
        takeUntil(this._destroyed$),
      )
      .subscribe((msg) => {
        this.handleMessage(msg);
      });

    this.widgetConfig = this._generateTamaroConfig();
    this._setupSentryScope();
  }

  ngOnDestroy(): void {
    this._cleanupSentryScope();
    this._destroyFrame();
    this.closeModal();
    this._state$.complete();
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }

  /**
   * Send the tamaro-config to the Tamaro-IFrame
   * and wait for message
   */
  protected initTamaroWidget(tamaro: HTMLIFrameElement): void {
    if (!tamaro.contentWindow) {
      this._msgIn$.error(
        new TamaroError(
          TamaroMessageSource.Host,
          'Could not initialize Tamaro Widget',
        ),
      );
      return;
    }

    // Setup message channel between here and iframe. We'll listen on
    // port1 and transfer port2 to the iframe.
    const channel = new MessageChannel();

    fromPortMessage<TamaroMessage>(channel.port1)
      .pipe(
        tap((msg) => {
          this._logs[msg.source].info(`${msg.type} - ${msg.name}`);
          if (msg.type === 'error') {
            throw new TamaroError(msg.source, msg.name);
          }
        }),
        takeUntil(merge(this._finalState$, this._destroyed$)),
      )
      .subscribe({
        next: (msg) => {
          this._msgIn$.next(msg);
        },
        error: (e) => {
          this._msgIn$.error(e);
        },
      });

    // Additionally, when a message is emitted on the `msgOut$` stream,
    // it should be sent to the iframe.
    this._msgOut$
      .pipe(takeUntil(merge(this._finalState$, this._destroyed$)))
      .subscribe((msg) => {
        channel.port1.postMessage(msg);
      });

    tamaro.contentWindow.postMessage(
      this.widgetConfig,
      '/', // restricts message to same-origin targets only
      [channel.port2],
    );
  }

  /**
   * Closing will remove the embedded iframe and emit events, based
   * on the payment status
   */
  protected closeModal() {
    // We only need to call close modal here, everything else should be picked up
    // by the RaiseNowModalService.
    this.closed.emit(false);
  }

  /**
   * Starts tamaro payment.
   */
  protected startPayment() {
    this._sendMessage(
      TamaroMessageType.Action,
      TamaroIframeAction.ValidateAndSend,
    );
  }

  /**
   * Sends an action message.
   *
   * @param type Message type
   * @param action Action of the message
   */
  private _sendMessage(
    type: TamaroMessageType.Action,
    action: TamaroIframeAction,
  ): void;
  private _sendMessage(
    type: TamaroMessageType,
    name: TamaroMessage['name'],
  ): void {
    this._msgOut$.next({
      name,
      type,
      source: TamaroMessageSource.Host,
    });
  }

  protected handleMessage(msg: TamaroMessage) {
    switch (msg.type) {
      case TamaroMessageType.Event:
        return this._handleEventMessage(msg);
      case TamaroMessageType.Result:
        return this._handleResultMessage(msg);
    }
  }

  private _handleEventMessage(msg: TamaroMessage) {
    switch (msg.name) {
      case TamaroEvent.AfterRender:
      case TamaroEvent.PaymentValidateError:
        this._state$.next(State.Ready);
        return;
      case TamaroEvent.BeforePaymentSend:
        this._state$.next(State.InProgress);
        return;
    }
  }

  private _handleResultMessage(msg: TamaroMessage) {
    switch (msg.name) {
      case TamaroResult.EpmsSuccess:
        this._onPaymentSuccess();
        return;
      case TamaroResult.EpmsFailed:
        this._onPaymentFailed();
        return;
    }
  }

  private _onPaymentSuccess() {
    this._state$.next(State.Done);
    this.finished.emit();
    this.closeModal();
  }

  private _onPaymentFailed() {
    this._state$.next(State.Failed);
  }

  protected initializationFailed(): void {
    this._msgIn$.error(
      new TamaroError(TamaroMessageSource.Host, 'Error loading iframe'),
    );
  }

  private _destroyFrame() {
    this._msgIn$.complete();
    this._msgOut$.complete();
    this._frameDestroyed$.next(true);
    this._frameDestroyed$.complete();
  }

  /**
   * Creates the configuration to be passed to the tamaro iframe.
   * @returns
   * Configuration for tamaro widget
   */
  private _generateTamaroConfig(): TamaroWidgetConfig {
    const { raisenow_tamaro_config, raisenow_tamaro_instance } =
      this.raiseNowModalService.getFormData();

    // Try to sanitize the passed raisenow tamaro instance, using `URL` context
    // since `RESOURCE_URL` can't be sanitized.
    // https://angular.io/guide/security#sanitization-and-security-contexts
    const tamaroInstance = isString(raisenow_tamaro_instance)
      ? this.sanitizer.sanitize(SecurityContext.URL, raisenow_tamaro_instance)
      : null;
    if (!tamaroInstance) {
      this.uploadError = true;
      this._msgIn$.error(
        new TamaroError(TamaroMessageSource.Host, 'Missing Tamaro Instance'),
      );
    }

    // If no config was sent from server, show error
    if (!raisenow_tamaro_config) {
      this.uploadError = true;
      this._msgIn$.error(
        new TamaroError(TamaroMessageSource.Host, 'Missing Tamaro Config'),
      );
    }

    const tamaroConfig = {
      ...raisenow_tamaro_config,
      showSubmitButton: this.useTamaroSubmitButton,
    };

    return {
      tamaroInstance: tamaroInstance ?? '<none>',
      tamaroConfig,
      cleanupSession: this._config.cleanupSession,
    };
  }

  private _setupSentryScope() {
    Sentry.setTag(SentryTamaroTag.Instance, this.widgetConfig.tamaroInstance);
  }

  private _cleanupSentryScope() {
    Sentry.setTag(SentryTamaroTag.Instance, undefined);
  }
}
